// Storage Usage Tracking Service
// Tracks combined Cloudinary + Firebase storage usage

import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, where, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { cloudinaryService } from './cloudinary';

export interface StorageUsage {
  organizationCode: string;
  organizationName: string;
  cloudinary: {
    used: number; // bytes
    limit: number; // bytes (calculated per org)
    percentage: number;
  };
  firebase: {
    used: number; // bytes
    limit: number; // bytes (calculated per org)
    percentage: number;
  };
  total: {
    used: number; // bytes
    limit: number; // bytes
    percentage: number;
  };
  breakdown: {
    posts: number;
    messages: number;
    profilePictures: number;
    events: number;
    other: number;
  };
  lastUpdated: Date;
}

const CLOUDINARY_TOTAL_LIMIT = 10 * 1024 * 1024 * 1024; // 10 GB total
const FIREBASE_TOTAL_LIMIT = 5 * 1024 * 1024 * 1024; // 5 GB total

class StorageUsageService {
  private static instance: StorageUsageService;

  private constructor() {}

  public static getInstance(): StorageUsageService {
    if (!StorageUsageService.instance) {
      StorageUsageService.instance = new StorageUsageService();
    }
    return StorageUsageService.instance;
  }

  /**
   * Get total number of active organizations
   */
  private async getActiveOrganizationCount(): Promise<number> {
    try {
      const orgsSnapshot = await getDocs(
        query(collection(db, 'organizations'), where('isActive', '==', true))
      );
      return Math.max(1, orgsSnapshot.size); // At least 1 to avoid division by zero
    } catch (error) {
      console.error('Error getting organization count:', error);
      return 1;
    }
  }

  /**
   * Get storage usage for a specific organization
   */
  public async getStorageUsage(organizationCode: string): Promise<StorageUsage | null> {
    try {
      const orgCount = await this.getActiveOrganizationCount();
      const cloudinaryLimit = CLOUDINARY_TOTAL_LIMIT / orgCount;
      const firebaseLimit = FIREBASE_TOTAL_LIMIT / orgCount;

      const usageDoc = await getDoc(doc(db, 'storage_usage', organizationCode));
      
      if (!usageDoc.exists()) {
        // Initialize if doesn't exist
        return await this.initializeStorageUsage(organizationCode, cloudinaryLimit, firebaseLimit);
      }

      const data = usageDoc.data();
      const cloudinaryUsed = data.cloudinary?.used || 0;
      const firebaseUsed = data.firebase?.used || 0;
      const totalUsed = cloudinaryUsed + firebaseUsed;
      const totalLimit = cloudinaryLimit + firebaseLimit;

      // Get organization name - organizations might be stored by code field, not doc ID
      let orgName = organizationCode;
      try {
        const orgsSnapshot = await getDocs(
          query(collection(db, 'organizations'), where('code', '==', organizationCode))
        );
        if (!orgsSnapshot.empty) {
          orgName = orgsSnapshot.docs[0].data().name || organizationCode;
        } else {
          // Fallback: try doc ID
          const orgDoc = await getDoc(doc(db, 'organizations', organizationCode));
          if (orgDoc.exists()) {
            orgName = orgDoc.data().name || organizationCode;
          }
        }
      } catch (error) {
        console.warn('Could not fetch organization name:', error);
      }

      return {
        organizationCode,
        organizationName: orgName,
        cloudinary: {
          used: cloudinaryUsed,
          limit: cloudinaryLimit,
          percentage: (cloudinaryUsed / cloudinaryLimit) * 100,
        },
        firebase: {
          used: firebaseUsed,
          limit: firebaseLimit,
          percentage: (firebaseUsed / firebaseLimit) * 100,
        },
        total: {
          used: totalUsed,
          limit: totalLimit,
          percentage: (totalUsed / totalLimit) * 100,
        },
        breakdown: data.breakdown || {
          posts: 0,
          messages: 0,
          profilePictures: 0,
          events: 0,
          other: 0,
        },
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
      };
    } catch (error) {
      console.error('Error getting storage usage:', error);
      return null;
    }
  }

  /**
   * Get all organizations' storage usage
   */
  public async getAllOrganizationsStorageUsage(): Promise<StorageUsage[]> {
    try {
      const orgsSnapshot = await getDocs(
        query(collection(db, 'organizations'), where('isActive', '==', true))
      );
      const orgCount = Math.max(1, orgsSnapshot.size);
      const cloudinaryLimit = CLOUDINARY_TOTAL_LIMIT / orgCount;
      const firebaseLimit = FIREBASE_TOTAL_LIMIT / orgCount;

      const usagePromises = orgsSnapshot.docs.map(async (orgDoc) => {
        const orgData = orgDoc.data();
        const orgCode = orgData.code || orgDoc.id;
        const orgName = orgData.name || orgCode;
        const usageDoc = await getDoc(doc(db, 'storage_usage', orgCode));
        const data = usageDoc.exists() ? usageDoc.data() : {};
        
        const cloudinaryUsed = data.cloudinary?.used || 0;
        const firebaseUsed = data.firebase?.used || 0;
        const totalUsed = cloudinaryUsed + firebaseUsed;
        const totalLimit = cloudinaryLimit + firebaseLimit;

        return {
          organizationCode: orgCode,
          organizationName: orgName,
          cloudinary: {
            used: cloudinaryUsed,
            limit: cloudinaryLimit,
            percentage: (cloudinaryUsed / cloudinaryLimit) * 100,
          },
          firebase: {
            used: firebaseUsed,
            limit: firebaseLimit,
            percentage: (firebaseUsed / firebaseLimit) * 100,
          },
          total: {
            used: totalUsed,
            limit: totalLimit,
            percentage: (totalUsed / totalLimit) * 100,
          },
          breakdown: data.breakdown || {
            posts: 0,
            messages: 0,
            profilePictures: 0,
            events: 0,
            other: 0,
          },
          lastUpdated: data.lastUpdated?.toDate() || new Date(),
        };
      });

      return await Promise.all(usagePromises);
    } catch (error) {
      console.error('Error getting all organizations storage usage:', error);
      return [];
    }
  }

  /**
   * Initialize storage usage document for an organization
   */
  private async initializeStorageUsage(
    organizationCode: string,
    cloudinaryLimit: number,
    firebaseLimit: number
  ): Promise<StorageUsage> {
    // Try to get organization name - organizations might be stored by code field, not doc ID
    let orgName = organizationCode;
    try {
      const orgsSnapshot = await getDocs(
        query(collection(db, 'organizations'), where('code', '==', organizationCode))
      );
      if (!orgsSnapshot.empty) {
        orgName = orgsSnapshot.docs[0].data().name || organizationCode;
      } else {
        // Fallback: try doc ID
        const orgDoc = await getDoc(doc(db, 'organizations', organizationCode));
        if (orgDoc.exists()) {
          orgName = orgDoc.data().name || organizationCode;
        }
      }
    } catch (error) {
      console.warn('Could not fetch organization name:', error);
    }

    const initialUsage: StorageUsage = {
      organizationCode,
      organizationName: orgName,
      cloudinary: {
        used: 0,
        limit: cloudinaryLimit,
        percentage: 0,
      },
      firebase: {
        used: 0,
        limit: firebaseLimit,
        percentage: 0,
      },
      total: {
        used: 0,
        limit: cloudinaryLimit + firebaseLimit,
        percentage: 0,
      },
      breakdown: {
        posts: 0,
        messages: 0,
        profilePictures: 0,
        events: 0,
        other: 0,
      },
      lastUpdated: new Date(),
    };

    await setDoc(doc(db, 'storage_usage', organizationCode), {
      cloudinary: { used: 0 },
      firebase: { used: 0 },
      breakdown: initialUsage.breakdown,
      lastUpdated: new Date(),
    });

    return initialUsage;
  }

  /**
   * Calculate and update storage usage from actual data for an organization
   */
  public async calculateStorageUsage(organizationCode: string): Promise<StorageUsage> {
    try {
      console.log(`📊 Calculating storage usage for organization: ${organizationCode}...`);

      const orgCount = await this.getActiveOrganizationCount();
      const cloudinaryLimit = CLOUDINARY_TOTAL_LIMIT / orgCount;
      const firebaseLimit = FIREBASE_TOTAL_LIMIT / orgCount;

      // Calculate Firebase Storage usage from Firestore metadata for this org
      const firebaseUsage = await this.calculateFirebaseUsage(organizationCode);
      
      // Get Cloudinary usage (try API first, fallback to estimation) for this org
      const cloudinaryUsage = await this.getCloudinaryUsage(organizationCode);

      const totalUsed = firebaseUsage + cloudinaryUsage;
      const totalLimit = cloudinaryLimit + firebaseLimit;

      // Try to get organization name - organizations might be stored by code field, not doc ID
      let orgName = organizationCode;
      try {
        const orgsSnapshot = await getDocs(
          query(collection(db, 'organizations'), where('code', '==', organizationCode))
        );
        if (!orgsSnapshot.empty) {
          orgName = orgsSnapshot.docs[0].data().name || organizationCode;
        } else {
          // Fallback: try doc ID
          const orgDoc = await getDoc(doc(db, 'organizations', organizationCode));
          if (orgDoc.exists()) {
            orgName = orgDoc.data().name || organizationCode;
          }
        }
      } catch (error) {
        console.warn('Could not fetch organization name:', error);
      }

      const usage: StorageUsage = {
        organizationCode,
        organizationName: orgName,
        cloudinary: {
          used: cloudinaryUsage,
          limit: cloudinaryLimit,
          percentage: (cloudinaryUsage / cloudinaryLimit) * 100,
        },
        firebase: {
          used: firebaseUsage,
          limit: firebaseLimit,
          percentage: (firebaseUsage / firebaseLimit) * 100,
        },
        total: {
          used: totalUsed,
          limit: totalLimit,
          percentage: (totalUsed / totalLimit) * 100,
        },
        breakdown: await this.calculateBreakdown(organizationCode),
        lastUpdated: new Date(),
      };

      // Save to Firestore
      await setDoc(doc(db, 'storage_usage', organizationCode), {
        cloudinary: { used: cloudinaryUsage },
        firebase: { used: firebaseUsage },
        breakdown: usage.breakdown,
        lastUpdated: new Date(),
      });

      console.log('✅ Storage usage calculated:', usage);
      return usage;
    } catch (error) {
      console.error('Error calculating storage usage:', error);
      throw error;
    }
  }

  /**
   * Calculate Firebase Storage usage by estimating from Firestore data for an organization
   */
  private async calculateFirebaseUsage(organizationCode: string): Promise<number> {
    try {
      let totalBytes = 0;

      // Helper to check if document belongs to organization
      const belongsToOrg = (data: any): boolean => {
        return data.organizationCode === organizationCode ||
               data.schoolCode === organizationCode ||
               data.classCode === organizationCode ||
               data.classGroup === organizationCode;
      };

      // Estimate from posts with images for this organization
      let postCount = 0;
      let postImageCount = 0;
      try {
        const postsSnapshot = await getDocs(
          query(collection(db, 'posts'), where('organizationCode', '==', organizationCode))
        );
        postCount = postsSnapshot.size;
        postsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.imageUrl && (data.imageUrl.includes('firebasestorage') || data.imageUrl.includes('cloudinary.com'))) {
            postImageCount++;
            totalBytes += data.imageUrl.includes('cloudinary.com') ? 600 * 1024 : 500 * 1024;
          }
        });
        console.log(`Found ${postCount} posts, ${postImageCount} with images`);
        
        // If we found very few posts, also check all posts to catch ones without organizationCode
        if (postCount < 10) {
          console.log('Few posts found, checking all posts...');
          const allPostsSnapshot = await getDocs(collection(db, 'posts'));
          console.log(`Checking ${allPostsSnapshot.size} total posts...`);
          allPostsSnapshot.forEach((doc) => {
            const data = doc.data();
            if (belongsToOrg(data) && data.imageUrl && (data.imageUrl.includes('firebasestorage') || data.imageUrl.includes('cloudinary.com'))) {
              // Only count if not already counted
              if (!postsSnapshot.docs.some(d => d.id === doc.id)) {
                postImageCount++;
                totalBytes += data.imageUrl.includes('cloudinary.com') ? 600 * 1024 : 500 * 1024;
              }
            }
          });
        }
        console.log(`Total: ${postCount} posts belonging to org, ${postImageCount} with images`);
      } catch (error) {
        // If query fails, try getting all posts and filtering
        console.log('Query by organizationCode failed, checking all posts...', error);
        const allPostsSnapshot = await getDocs(collection(db, 'posts'));
        console.log(`Checking ${allPostsSnapshot.size} total posts...`);
        allPostsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (belongsToOrg(data)) {
            postCount++;
            if (data.imageUrl && (data.imageUrl.includes('firebasestorage') || data.imageUrl.includes('cloudinary.com'))) {
              postImageCount++;
              totalBytes += data.imageUrl.includes('cloudinary.com') ? 600 * 1024 : 500 * 1024;
            }
          }
        });
        console.log(`Found ${postCount} posts belonging to org, ${postImageCount} with images`);
      }

      // Estimate from messages with images for this organization
      let messageCount = 0;
      let messageImageCount = 0;
      try {
        const conversationsSnapshot = await getDocs(
          query(collection(db, 'conversations'), where('organizationCode', '==', organizationCode))
        );
        console.log(`Found ${conversationsSnapshot.size} conversations with organizationCode=${organizationCode}`);
        
        for (const convDoc of conversationsSnapshot.docs) {
          const messagesSnapshot = await getDocs(
            collection(db, 'conversations', convDoc.id, 'messages')
          );
          messageCount += messagesSnapshot.size;
          for (const msgDoc of messagesSnapshot.docs) {
            const msgData = msgDoc.data();
            if (msgData.imageUrl && (msgData.imageUrl.includes('firebasestorage') || msgData.imageUrl.includes('cloudinary.com'))) {
              messageImageCount++;
              totalBytes += 300 * 1024; // Estimate 300KB per chat image
            }
          }
        }
        
        // If we found very few conversations, also check all conversations to catch ones without organizationCode
        if (conversationsSnapshot.size < 5) {
          console.log('Few conversations found, checking all conversations for participants...');
          const allConvsSnapshot = await getDocs(collection(db, 'conversations'));
          for (const convDoc of allConvsSnapshot.docs) {
            const convData = convDoc.data();
            // Check if conversation belongs to org OR if participants belong to org
            let belongsToOrgCheck = belongsToOrg(convData);
            
            if (!belongsToOrgCheck && convData.participants && Array.isArray(convData.participants)) {
              // Check if any participant belongs to this organization
              for (const participantId of convData.participants.slice(0, 2)) { // Check first 2 participants
                try {
                  const userDoc = await getDoc(doc(db, 'users', participantId));
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    if (belongsToOrg(userData)) {
                      belongsToOrgCheck = true;
                      break;
                    }
                  }
                } catch (e) {
                  // Skip if can't get user
                }
              }
            }
            
            if (belongsToOrgCheck) {
              const messagesSnapshot = await getDocs(
                collection(db, 'conversations', convDoc.id, 'messages')
              );
              messageCount += messagesSnapshot.size;
              for (const msgDoc of messagesSnapshot.docs) {
                const msgData = msgDoc.data();
                if (msgData.imageUrl && (msgData.imageUrl.includes('firebasestorage') || msgData.imageUrl.includes('cloudinary.com'))) {
                  messageImageCount++;
                  totalBytes += 300 * 1024;
                }
              }
            }
          }
        }
        console.log(`Found ${messageCount} total messages, ${messageImageCount} with images`);
      } catch (error) {
        // If query fails, try getting all conversations and filtering
        console.log('Query conversations by organizationCode failed, checking all...', error);
        const allConvsSnapshot = await getDocs(collection(db, 'conversations'));
        for (const convDoc of allConvsSnapshot.docs) {
          const convData = convDoc.data();
          if (belongsToOrg(convData)) {
            const messagesSnapshot = await getDocs(
              collection(db, 'conversations', convDoc.id, 'messages')
            );
            messageCount += messagesSnapshot.size;
            for (const msgDoc of messagesSnapshot.docs) {
              const msgData = msgDoc.data();
              if (msgData.imageUrl && (msgData.imageUrl.includes('firebasestorage') || msgData.imageUrl.includes('cloudinary.com'))) {
                messageImageCount++;
                totalBytes += 300 * 1024;
              }
            }
          }
        }
        console.log(`Found ${messageCount} total messages, ${messageImageCount} with images`);
      }

      // Estimate profile pictures for users in this organization
      try {
        const usersSnapshot = await getDocs(
          query(
            collection(db, 'users'),
            where('organizationCode', '==', organizationCode),
            where('profilePictureUrl', '!=', null)
          )
        );
        usersSnapshot.forEach(() => {
          totalBytes += 200 * 1024; // Estimate 200KB per profile picture
        });
      } catch (error) {
        // If query fails, try getting all users and filtering
        console.log('Query users by organizationCode failed, checking all...');
        const allUsersSnapshot = await getDocs(collection(db, 'users'));
        allUsersSnapshot.forEach((doc) => {
          const data = doc.data();
          if (belongsToOrg(data) && data.profilePictureUrl) {
            totalBytes += 200 * 1024;
          }
        });
      }

      console.log(`📊 Calculated Firebase usage for ${organizationCode}: ${totalBytes} bytes`);
      return totalBytes;
    } catch (error) {
      console.error('Error calculating Firebase usage:', error);
      return 0;
    }
  }

  /**
   * Get Cloudinary usage (try API, fallback to estimation) for an organization
   */
  private async getCloudinaryUsage(organizationCode: string): Promise<number> {
    try {
      // Try to get from Cloudinary API and divide by org count
      const apiUsage = await cloudinaryService.getAccountUsage();
      if (apiUsage !== null && apiUsage > 0) {
        const orgCount = await this.getActiveOrganizationCount();
        const perOrgUsage = apiUsage / orgCount;
        console.log(`📊 Cloudinary API usage: ${apiUsage} bytes total, ${perOrgUsage} bytes per org`);
        return perOrgUsage; // Divide total by number of orgs
      }

      // Fallback: Estimate from posts with Cloudinary URLs for this organization
      let totalBytes = 0;
      
      // Helper to check if document belongs to organization
      const belongsToOrg = (data: any): boolean => {
        return data.organizationCode === organizationCode ||
               data.schoolCode === organizationCode ||
               data.classCode === organizationCode ||
               data.classGroup === organizationCode;
      };

      try {
        const postsSnapshot = await getDocs(
          query(collection(db, 'posts'), where('organizationCode', '==', organizationCode))
        );
        postsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.imageUrl && data.imageUrl.includes('cloudinary.com')) {
            totalBytes += 600 * 1024; // Estimate 600KB per Cloudinary image
          }
        });
      } catch (error) {
        // If query fails, try getting all posts and filtering
        console.log('Query posts by organizationCode failed, checking all...');
        const allPostsSnapshot = await getDocs(collection(db, 'posts'));
        allPostsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (belongsToOrg(data) && data.imageUrl && data.imageUrl.includes('cloudinary.com')) {
            totalBytes += 600 * 1024;
          }
        });
      }

      // Estimate from events for this organization
      let eventCount = 0;
      let eventImageCount = 0;
      try {
        const eventsSnapshot = await getDocs(
          query(collection(db, 'events'), where('organizationCode', '==', organizationCode))
        );
        eventCount = eventsSnapshot.size;
        eventsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.imageUrl && data.imageUrl.includes('cloudinary.com')) {
            eventImageCount++;
            totalBytes += 800 * 1024; // Estimate 800KB per event image
          }
        });
        console.log(`Found ${eventCount} events, ${eventImageCount} with Cloudinary images`);
        
        // If we found very few events, also check all events
        if (eventCount < 5) {
          console.log('Few events found, checking all events...');
          const allEventsSnapshot = await getDocs(collection(db, 'events'));
          allEventsSnapshot.forEach((doc) => {
            const data = doc.data();
            if (belongsToOrg(data) && data.imageUrl && data.imageUrl.includes('cloudinary.com')) {
              // Only count if not already counted
              if (!eventsSnapshot.docs.some(d => d.id === doc.id)) {
                eventImageCount++;
                totalBytes += 800 * 1024;
              }
            }
          });
        }
        console.log(`Total: ${eventCount} events belonging to org, ${eventImageCount} with images`);
      } catch (error) {
        // If query fails, try getting all events and filtering
        console.log('Query events by organizationCode failed, checking all...', error);
        const allEventsSnapshot = await getDocs(collection(db, 'events'));
        allEventsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (belongsToOrg(data) && data.imageUrl && data.imageUrl.includes('cloudinary.com')) {
            eventImageCount++;
            totalBytes += 800 * 1024;
          }
        });
        console.log(`Found ${eventImageCount} events with images`);
      }

      console.log(`📊 Estimated Cloudinary usage for ${organizationCode}: ${totalBytes} bytes`);
      return totalBytes;
    } catch (error) {
      console.error('Error getting Cloudinary usage:', error);
      return 0;
    }
  }

  /**
   * Calculate storage breakdown by type for an organization
   */
  private async calculateBreakdown(organizationCode: string): Promise<StorageUsage['breakdown']> {
    const breakdown = {
      posts: 0,
      messages: 0,
      profilePictures: 0,
      events: 0,
      other: 0,
    };

    try {
      // Helper to check if document belongs to organization
      const belongsToOrg = (data: any): boolean => {
        return data.organizationCode === organizationCode ||
               data.schoolCode === organizationCode ||
               data.classCode === organizationCode ||
               data.classGroup === organizationCode;
      };

      // Count posts with images for this organization
      let postBreakdownCount = 0;
      try {
        const postsSnapshot = await getDocs(
          query(collection(db, 'posts'), where('organizationCode', '==', organizationCode))
        );
        console.log(`Breakdown: Found ${postsSnapshot.size} posts`);
        postsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.imageUrl) {
            postBreakdownCount++;
            breakdown.posts += data.imageUrl.includes('cloudinary.com') ? 600 * 1024 : 500 * 1024;
          }
        });
        
        // If we found very few posts, also check all posts
        if (postsSnapshot.size < 10) {
          console.log('Breakdown: Checking all posts...');
          const allPostsSnapshot = await getDocs(collection(db, 'posts'));
          allPostsSnapshot.forEach((doc) => {
            const data = doc.data();
            // Check if already processed
            const alreadyProcessed = postsSnapshot.docs.some(d => d.id === doc.id);
            if (!alreadyProcessed && belongsToOrg(data) && data.imageUrl) {
              postBreakdownCount++;
              breakdown.posts += data.imageUrl.includes('cloudinary.com') ? 600 * 1024 : 500 * 1024;
            }
          });
        }
        console.log(`Breakdown: Found ${postBreakdownCount} posts with images`);
      } catch (error) {
        // Fallback: check all posts
        console.log('Breakdown: Query failed, checking all posts...', error);
        const allPostsSnapshot = await getDocs(collection(db, 'posts'));
        allPostsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (belongsToOrg(data) && data.imageUrl) {
            postBreakdownCount++;
            breakdown.posts += data.imageUrl.includes('cloudinary.com') ? 600 * 1024 : 500 * 1024;
          }
        });
        console.log(`Breakdown: Found ${postBreakdownCount} posts with images`);
      }

      // Count messages with images for this organization
      let messageBreakdownCount = 0;
      try {
        const conversationsSnapshot = await getDocs(
          query(collection(db, 'conversations'), where('organizationCode', '==', organizationCode))
        );
        console.log(`Breakdown: Found ${conversationsSnapshot.size} conversations`);
        
        for (const convDoc of conversationsSnapshot.docs) {
          const messagesSnapshot = await getDocs(
            collection(db, 'conversations', convDoc.id, 'messages')
          );
          for (const msgDoc of messagesSnapshot.docs) {
            const msgData = msgDoc.data();
            if (msgData.imageUrl) {
              messageBreakdownCount++;
              breakdown.messages += 300 * 1024;
            }
          }
        }
        
        // If we found very few conversations, also check all conversations
        if (conversationsSnapshot.size < 5) {
          console.log('Breakdown: Checking all conversations...');
          const allConvsSnapshot = await getDocs(collection(db, 'conversations'));
          for (const convDoc of allConvsSnapshot.docs) {
            const convData = convDoc.data();
            // Check if already processed
            const alreadyProcessed = conversationsSnapshot.docs.some(d => d.id === convDoc.id);
            if (!alreadyProcessed && belongsToOrg(convData)) {
              const messagesSnapshot = await getDocs(
                collection(db, 'conversations', convDoc.id, 'messages')
              );
              for (const msgDoc of messagesSnapshot.docs) {
                const msgData = msgDoc.data();
                if (msgData.imageUrl) {
                  messageBreakdownCount++;
                  breakdown.messages += 300 * 1024;
                }
              }
            }
          }
        }
        console.log(`Breakdown: Found ${messageBreakdownCount} messages with images`);
      } catch (error) {
        // Fallback: check all conversations
        console.log('Breakdown: Query failed, checking all conversations...', error);
        const allConvsSnapshot = await getDocs(collection(db, 'conversations'));
        for (const convDoc of allConvsSnapshot.docs) {
          const convData = convDoc.data();
          if (belongsToOrg(convData)) {
            const messagesSnapshot = await getDocs(
              collection(db, 'conversations', convDoc.id, 'messages')
            );
            for (const msgDoc of messagesSnapshot.docs) {
              const msgData = msgDoc.data();
              if (msgData.imageUrl) {
                messageBreakdownCount++;
                breakdown.messages += 300 * 1024;
              }
            }
          }
        }
        console.log(`Breakdown: Found ${messageBreakdownCount} messages with images`);
      }

      // Count profile pictures for users in this organization
      try {
        const usersSnapshot = await getDocs(
          query(
            collection(db, 'users'),
            where('organizationCode', '==', organizationCode),
            where('profilePictureUrl', '!=', null)
          )
        );
        usersSnapshot.forEach(() => {
          breakdown.profilePictures += 200 * 1024;
        });
      } catch (error) {
        // Fallback: check all users
        const allUsersSnapshot = await getDocs(collection(db, 'users'));
        allUsersSnapshot.forEach((doc) => {
          const data = doc.data();
          if (belongsToOrg(data) && data.profilePictureUrl) {
            breakdown.profilePictures += 200 * 1024;
          }
        });
      }

      // Count events with images for this organization
      let eventBreakdownCount = 0;
      try {
        const eventsSnapshot = await getDocs(
          query(collection(db, 'events'), where('organizationCode', '==', organizationCode))
        );
        console.log(`Breakdown: Found ${eventsSnapshot.size} events`);
        eventsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.imageUrl) {
            eventBreakdownCount++;
            breakdown.events += 800 * 1024;
          }
        });
        
        // If we found very few events, also check all events
        if (eventsSnapshot.size < 5) {
          console.log('Breakdown: Checking all events...');
          const allEventsSnapshot = await getDocs(collection(db, 'events'));
          allEventsSnapshot.forEach((doc) => {
            const data = doc.data();
            // Check if already processed
            const alreadyProcessed = eventsSnapshot.docs.some(d => d.id === doc.id);
            if (!alreadyProcessed && belongsToOrg(data) && data.imageUrl) {
              eventBreakdownCount++;
              breakdown.events += 800 * 1024;
            }
          });
        }
        console.log(`Breakdown: Found ${eventBreakdownCount} events with images`);
      } catch (error) {
        // Fallback: check all events
        console.log('Breakdown: Query failed, checking all events...', error);
        const allEventsSnapshot = await getDocs(collection(db, 'events'));
        allEventsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (belongsToOrg(data) && data.imageUrl) {
            eventBreakdownCount++;
            breakdown.events += 800 * 1024;
          }
        });
        console.log(`Breakdown: Found ${eventBreakdownCount} events with images`);
      }

      console.log(`📊 Breakdown for ${organizationCode}:`, breakdown);
    } catch (error) {
      console.error('Error calculating breakdown:', error);
    }

    return breakdown;
  }

  /**
   * Increment storage usage when file is uploaded
   */
  public async incrementUsage(
    organizationCode: string,
    service: 'cloudinary' | 'firebase',
    bytes: number,
    type: 'posts' | 'messages' | 'profilePictures' | 'events' | 'other'
  ): Promise<void> {
    try {
      const usageDoc = doc(db, 'storage_usage', organizationCode);
      const currentUsage = await this.getStorageUsage(organizationCode);
      
      if (!currentUsage) {
        const orgCount = await this.getActiveOrganizationCount();
        const cloudinaryLimit = CLOUDINARY_TOTAL_LIMIT / orgCount;
        const firebaseLimit = FIREBASE_TOTAL_LIMIT / orgCount;
        await this.initializeStorageUsage(organizationCode, cloudinaryLimit, firebaseLimit);
      }

      await updateDoc(usageDoc, {
        [`${service}.used`]: increment(bytes),
        [`breakdown.${type}`]: increment(bytes),
        lastUpdated: new Date(),
      });
    } catch (error) {
      console.error('Error incrementing storage usage:', error);
    }
  }

  /**
   * Decrement storage usage when file is deleted
   */
  public async decrementUsage(
    organizationCode: string,
    service: 'cloudinary' | 'firebase',
    bytes: number,
    type: 'posts' | 'messages' | 'profilePictures' | 'events' | 'other'
  ): Promise<void> {
    try {
      const usageDoc = doc(db, 'storage_usage', organizationCode);
      const currentUsage = await this.getStorageUsage(organizationCode);
      
      if (!currentUsage) return;

      const newUsed = Math.max(0, (currentUsage[service].used || 0) - bytes);
      const newBreakdown = Math.max(0, (currentUsage.breakdown[type] || 0) - bytes);

      await updateDoc(usageDoc, {
        [`${service}.used`]: newUsed,
        [`breakdown.${type}`]: newBreakdown,
        lastUpdated: new Date(),
      });
    } catch (error) {
      console.error('Error decrementing storage usage:', error);
    }
  }

  /**
   * Reset storage usage to zero (after cleanup) for an organization
   */
  public async resetUsage(organizationCode: string): Promise<void> {
    try {
      await setDoc(doc(db, 'storage_usage', organizationCode), {
        cloudinary: { used: 0 },
        firebase: { used: 0 },
        breakdown: {
          posts: 0,
          messages: 0,
          profilePictures: 0,
          events: 0,
          other: 0,
        },
        lastUpdated: new Date(),
      });
    } catch (error) {
      console.error('Error resetting storage usage:', error);
    }
  }

  /**
   * Format bytes to human-readable string
   */
  public formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
}

export const storageUsageService = StorageUsageService.getInstance();
export default StorageUsageService;

